import React, { useEffect } from "react";
import { Form, Input, Button, Card, Typography, Select, Row, Col } from "antd";
import { User, Mail, Phone, IdCard, LockKeyhole, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { loginSuccess, signup } from "../store/slices/authSlice";
import useLocation from "../hooks/useLocation";
import axios from 'axios';

const { Title, Text } = Typography;
const { Option } = Select;

const SignUp = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.auth);

  const {
    countries,
    states,
    cities,
    selectedCountry,
    selectedState,
    selectedCity,
    setSelectedCountry,
    setSelectedState,
    setSelectedCity,
    loading: locationLoading,
  } = useLocation();

  // keep form.setFieldsValue effect so form picks up selection changes
  useEffect(() => {
    form.setFieldsValue({
      country: selectedCountry || undefined,
      state: selectedState || undefined,
      city: selectedCity || undefined,
    });
  }, [selectedCountry, selectedState, selectedCity, form]);


  const onFinish = async (values) => {
    try {
      // console.log("Form submitted:", values);
      const result = await dispatch(signup(values)).unwrap();
      // console.log("Signup successful:", result);
      if (result && (result.status === true || result.status === "success")) {
        toast.success(
          "Successfully Registered! Check your email for the verification code."
        );
        navigate("/verify-email", {
          replace: true,
          state: { email: values.email, phone: values.phone, fromSignup: true },
        });
      }
    } catch (error) {
      console.error("Signup failed:", error);
      toast.error(error.message || "Signup failed. Please try again.");
    }
  };

  const onFinishFailed = (errorInfo) => {
    // console.log("Failed:", errorInfo);
    toast.error(`Please check the form for ${errorInfo} and try again.`);
  };

  return (
    <div className="min-h-[100dvh] flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-2xl">
        <Card className="shadow-2xl border-0 rounded-2xl bg-[#141c31] backdrop-blur-sm p-2">
          <div className="text-center mb-6">
            <Title
              level={2}
              className="!text-white mb-1 font-serif text-2xl sm:text-3xl md:text-4xl"
            >
              Advanced Data Analysis & Anomaly Detection
            </Title>
            <Text className="text-white text-sm sm:text-base font-sans">
              Sign up to access your dashboard
            </Text>
          </div>

          <Form
            form={form}
            name="SignUp"
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            layout="vertical"
          >
            <Row gutter={[12]}>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  label={
                    <span className="text-gray-200 font-medium text-base">
                      First Name
                    </span>
                  }
                  name="firstName"
                  rules={[
                    { required: true, message: "Please input your name!" },
                    {
                      min: 2,
                      message: "Name must be at least 2 characters long!",
                    },
                  ]}
                >
                  <Input
                    prefix={<User className="text-gray-500" />}
                    placeholder="John"
                    size="large"
                    className="h-auto min-h-[40px] bg-gray-50 border-gray-200 rounded-lg"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  label={
                    <span className="text-gray-200 font-medium text-base">
                      Last Name
                    </span>
                  }
                  name="lastName"
                  rules={[
                    { required: true, message: "Please input your name!" },
                  ]}
                >
                  <Input
                    prefix={<User className="text-gray-500" />}
                    placeholder="Doe"
                    size="large"
                    className="h-auto min-h-[40px] bg-gray-50 border-gray-200 rounded-lg"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  className="mt-1"
                  label={
                    <span className="text-gray-200 font-medium text-base">
                      Email
                    </span>
                  }
                  name="email"
                  rules={[
                    {
                      type: "email",
                      message: "Please enter a valid email address!",
                    },
                    { required: true, message: "Please input your email!" },
                  ]}
                >
                  <Input
                    prefix={<Mail className="text-gray-500" />}
                    placeholder="john.doe@company.com"
                    size="large"
                    className="h-auto min-h-[40px] bg-gray-50 border-gray-200 rounded-lg"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  label={
                    <span className="text-gray-200 font-medium text-base">
                      Phone
                    </span>
                  }
                  name="phone"
                  rules={[
                    {
                      required: true,
                      message: "Please input your phone number!",
                    },
                    {
                      pattern: /^[0-9]{10}$/,
                      message: "Phone number must be exactly 10 digits!",
                    },
                  ]}
                >
                  <Input
                    prefix={<Phone className="text-gray-500" />}
                    placeholder="9870000000"
                    size="large"
                    className="h-auto min-h-[40px] bg-gray-50 border-gray-200 rounded-lg"
                    maxLength={10}
                  />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name="country"
                      label={
                        <span className="text-gray-200 font-medium text-base">
                          Country
                        </span>
                      }
                      rules={[
                        {
                          required: true,
                          message: "Please select your country!",
                        },
                      ]}
                    >
                      <Select
                        placeholder="Select your country"
                        size="large"
                        value={selectedCountry || undefined}
                        onChange={(val) => setSelectedCountry(val)}
                        loading={locationLoading}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          (option?.children ?? "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        className="h-10 bg-gray-50 border-gray-200 rounded-lg"
                        allowClear
                      >
                        {countries.map((c) => (
                          // we use country.name as the value to work with CountriesNow
                          <Option key={c.name} value={c.name}>
                            {c.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name="state"
                      label={
                        <span className="text-gray-200 font-medium text-base">
                          State
                        </span>
                      }
                      rules={[
                        { required: true, message: "Please enter your state!" },
                      ]}
                    >
                      <Select
                        placeholder="Select state"
                        size="large"
                        value={selectedState || undefined}
                        onChange={(val) => setSelectedState(val)}
                        disabled={!selectedCountry}
                        loading={locationLoading}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          (option?.children ?? "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        className="h-10 bg-gray-50 border-gray-200 rounded-lg"
                        allowClear
                      >
                        {states.map((s) => (
                          <Option key={s} value={s}>
                            {s}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name="city"
                      label={
                        <span className="text-gray-200 font-medium text-base">
                          City
                        </span>
                      }
                      rules={[
                        { required: true, message: "Please enter your city!" },
                      ]}
                    >
                      <Select
                        placeholder="Select city"
                        size="large"
                        value={selectedCity || undefined}
                        onChange={(val) => setSelectedCity(val)}
                        disabled={!selectedState}
                        loading={locationLoading}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          (option?.children ?? "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        className="h-10 bg-gray-50 border-gray-200 rounded-lg"
                        allowClear
                      >
                        {cities.map((ct, idx) => (
                          <Option key={`${ct}-${idx}`} value={ct}>
                            {ct}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              </Col>

              

              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="domain"
                  label={
                    <span className="text-gray-200 font-medium text-base">
                      Domain
                    </span>
                  }
                  rules={[{ required: true, message: "Please select domain!" }]}
                >
                  <Select
                    showSearch
                    placeholder="Select your domain"
                    size="large"
                    className="h-10 bg-gray-50 border-gray-200 rounded-lg"
                  >
                    <Option value="finance">Finance</Option>
                    <Option value="it">IT</Option>
                    <Option value="audit head">Audit Head</Option>
                    <Option value="cxo">CXO</Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  label={
                    <span className="text-gray-200 font-medium text-base">
                      Organization
                    </span>
                  }
                  name="organization"
                  rules={[
                    {
                      required: true,
                      message: "Please input your organization!",
                    },
                    {
                      min: 2,
                      message:
                        "Organization must be at least 2 characters long!",
                    },
                  ]}
                >
                  <Input
                    prefix={<IdCard className="text-gray-500" />}
                    placeholder="Enter your organization name"
                    size="large"
                    className="h-auto min-h-[40px] bg-gray-50 border-gray-200 rounded-lg"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="password"
                  label={
                    <span className="text-gray-200 font-medium text-base">
                      Password
                    </span>
                  }
                  rules={[
                    { required: true, message: "Please input your password!" },
                    {
                      min: 8,
                      max: 15,
                      message:
                        "Password must be between 8 and 15 characters long!",
                    },
                    {
                      pattern:
                        /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*]).+$/,
                      message:
                        "Password must contain at least 1 uppercase letters, 1 special characters, 1 numbers, and 1 lowercase letters!",
                    },
                  ]}
                >
                  <Input.Password
                    prefix={<LockKeyhole className="text-gray-500" />}
                    placeholder="Password"
                    size="large"
                    className="h-auto min-h-[40px] bg-gray-50 border-gray-200 rounded-lg"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="confirm"
                  label={
                    <span className="text-gray-200 font-medium text-base">
                      Confirm Password
                    </span>
                  }
                  dependencies={["password"]}
                  hasFeedback
                  rules={[
                    {
                      required: true,
                      message: "Please confirm your password!",
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("password") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error(
                            "The two passwords that you entered do not match!"
                          )
                        );
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    prefix={<LockKeyhole className="text-gray-500" />}
                    placeholder="Confirm Password"
                    size="large"
                    className="h-auto min-h-[40px] bg-gray-50 border-gray-200 rounded-lg"
                  />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item className="mb-0 pt-4">
                  {/* Sign Up Button and Login Link */}
                  <div className="flex items-center justify-center gap-4 flex-wrap">
                    <Button
                      htmlType="submit"
                      size="large"
                      disabled={loading}
                      className=" w-full !bg-gradient-to-r from-pink-600 to-purple-600 hover:!from-pink-700 hover:!to-purple-700 !text-white py-2 sm:py-3 text-sm sm:text-base transition-all duration-200 shadow-lg hover:!shadow-pink-500/25"
                    >
                      {loading ? "Creating Account..." : "SIGN UP"}
                    </Button>
                    <p className="text-white text-base">
                      Already Have account?{" "}
                    </p>
                    <button
                      type="button"
                      onClick={() => navigate("/login")}
                      className="font-semibold text-blue-500 hover:text-blue-400 transition-colors text-lg"
                    >
                      Login
                    </button>
                  </div>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default SignUp;
